import {
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  StyledFoodDetails,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import Button from "components/Buttons/Button"
import FoodImage from "components/foodDetails/share/FoodImage"
import { useState } from "react"
import { useBackToHome } from "features/meal/hooks"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import ScheduleConfigFormSection from "components/foodDetails/share/ScheduleConfigFormSection"
import { useCreateSchedule } from "features/schedule/hooks/CRUDSchedule"
import useScheduleFormSection from "features/schedule/hooks/useScheduleFormSection"
import { validateScheduleConfig } from "utils/validator"
import PopConfirm from "components/PopConfirm"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import useLogMealFromFood from "features/meal/hooks/logMeal/useLogMealFromFood"
import { IOpenFoodFactFood } from "model/food/types/openFoodFact.types"
import useOpenFoodFactForm from "features/food/hooks/openFoodFact/useOpenFoodFactForm"
import OpenFoodFactNameAndPortion from "components/foodDetails/share/OpenFoodFactNameAndPortion"
import OpenFoodFactDescription from "components/foodDetails/share/OpenFoodFactDescription"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { FoodCreationMethod } from "model/food/types/mealPayload.types"
import EditNutrientForm from "../share/EditNutrientForm"
import EditableNutrientInformation from "../share/EditableNutrientInformation"

interface IProps {
  onBack: () => void
  food: IOpenFoodFactFood
  foodCreationMethod?: FoodCreationMethod
}

const OpenFoodFactFoodDetailsWithSchedule = (props: IProps) => {
  const { onBack, food, foodCreationMethod } = props

  const [displaySection, setDisplaySection] = useState<
    "FOOD_FORM" | "EDIT_NUTRITION_FORM"
  >("FOOD_FORM")

  const { openFoodFact, onPortionChange, updateFoodAsManualNutrition } =
    useOpenFoodFactForm(food)

  const [isPortionValid, setIsPortionValid] = useState(true)
  const [hasSchedule, setHasSchedule] = useState(false)

  const onPortionChangeHandler = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const {
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    scheduleConfig,
    isNoEndDate,
    onNoEndDateChange,
    getCreateSchedulePayloadFromFood,
  } = useScheduleFormSection()

  const backToHome = useBackToHome()
  const { handleLogMealFromFood, logMealLoading } =
    useLogMealFromFood(backToHome)
  const { handleCreateSchedule, createScheduleLoading } =
    useCreateSchedule(backToHome)

  const [isPopConfirmScheduleOpen, setIsPopConfirmScheduleOpen] =
    useState(false)

  const onSubmit = (food: IOpenFoodFactFood) => {
    if (hasSchedule) {
      const schedulePayload = getCreateSchedulePayloadFromFood(food)
      if (schedulePayload) {
        handleCreateSchedule(schedulePayload, foodCreationMethod)
      }
    } else {
      handleLogMealFromFood(food, foodCreationMethod)
    }
  }

  const isScheduleValid = hasSchedule
    ? validateScheduleConfig(scheduleConfig, !isNoEndDate)
    : true

  if (displaySection === "EDIT_NUTRITION_FORM") {
    return (
      <EditNutrientForm
        onClose={() => {
          setDisplaySection("FOOD_FORM")
        }}
        meal={openFoodFact}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  return (
    <StyledFoodDetails>
      <FoodDetailsContent>
        <FoodImage previewImageUrl={food.previewImageUrl}>
          <TopNavWrapper>
            <FoodDetailsHeader>
              <BackButton onClick={onBack}>
                <ChevronLeftBtn />
              </BackButton>

              <div style={{ width: 24, height: 24 }}></div>
            </FoodDetailsHeader>
          </TopNavWrapper>
        </FoodImage>

        <FoodDetailsInfo>
          <OpenFoodFactNameAndPortion
            food={openFoodFact}
            unitValue={openFoodFact.unit}
            portionValue={openFoodFact.portion}
            onPortionChange={onPortionChangeHandler}
            setIsPortionValid={setIsPortionValid}
            calories={openFoodFact.calorie}
            gram={openFoodFact.gram}
          />

          <EditableNutrientInformation
            entry={openFoodFact}
            setEditMode={() => setDisplaySection("EDIT_NUTRITION_FORM")}
          />

          <ScheduleConfigFormSection
            scheduleConfig={scheduleConfig}
            onDayOfWeekChange={onDayOfWeekChange}
            onSchedulePeriodChange={onSchedulePeriodChange}
            onScheduleTypeChange={onScheduleTypeChange}
            isNoEndDate={isNoEndDate}
            onNoEndDateChange={onNoEndDateChange}
            hasSchedule={hasSchedule}
            setHasSchedule={setHasSchedule}
          />

          <OpenFoodFactDescription />
        </FoodDetailsInfo>
      </FoodDetailsContent>

      <AppStyleCtaBox>
        <Button
          onClick={() => {
            if (hasSchedule) {
              setIsPopConfirmScheduleOpen(true)
            } else {
              onSubmit(openFoodFact)
            }
          }}
          style={{ width: "100%" }}
          $type="primary"
          disabled={!isPortionValid || !isScheduleValid}
          loading={logMealLoading}
        >
          Log this item
        </Button>
      </AppStyleCtaBox>

      <PopConfirm
        cancelText="No"
        confirmText="Yes"
        isOpen={isPopConfirmScheduleOpen}
        title="Add Scheduled Meal?"
        onCancel={() => setIsPopConfirmScheduleOpen(false)}
        onConfirm={() => {
          onSubmit(openFoodFact)
        }}
        type="info"
        loading={createScheduleLoading}
      >
        <div style={{ textAlign: "center" }}>
          This meal will be logged automatically on your selected days. Do you
          want to proceed?
        </div>
      </PopConfirm>
    </StyledFoodDetails>
  )
}

export default OpenFoodFactFoodDetailsWithSchedule
